/**
 * PromoterInviteCTA.tsx - Promoter Invitation Call-to-Action Component
 * 
 * This component displays compelling call-to-action messaging to encourage
 * visitors to become promoters with benefits, incentives, and motivation.
 * 
 * Key Features:
 * - Compelling promotional messaging and headlines
 * - Benefits and incentives for becoming a promoter
 * - Social proof and success stories
 * - Reward system explanation and motivation
 * - Visual elements and engaging design
 * - Action-oriented language and urgency
 * 
 * Promotional Messaging:
 * - Clear value proposition for promoters
 * - Benefits of joining the fundraising effort
 * - Reward opportunities and achievements
 * - Impact messaging and cause connection
 * - Community and social elements
 * 
 * Incentive Display:
 * - Reward tiers and achievement opportunities
 * - Recognition and social status benefits
 * - Community involvement and impact
 * - Personal goal setting and achievement
 * - Competitive elements and gamification
 * 
 * Visual Design:
 * - Eye-catching headlines and messaging
 * - Benefit icons and visual elements
 * - Progress indicators and success metrics
 * - Call-to-action buttons and forms
 * - Responsive design for all devices
 * 
 * Props: None (uses MobX store for fundraiser context)
 * 
 * Dependencies: MobX store, promotional messaging, visual elements
 */

import React from 'react';
import styled from 'styled-components';
import { observer } from 'mobx-react-lite';
import { Heart } from 'lucide-react';
import { fundraiserStore } from '../../stores/FundraiserStore';
import { DonationFormType, IconType } from '../../enums';

const CTAContainer = styled.div<{ customColors?: any }>`
  background: ${props => props.customColors?.accent || 'linear-gradient(135deg, #f0fdf4, #ecfdf5)'};
  border-radius: 16px;
  padding: 32px;
  text-align: center;
  border: 1px solid #bbf7d0;
  box-shadow: 0 8px 32px rgba(16, 185, 129, 0.1);
  display: flex;
  flex-direction: column;
  min-height: 280px;
  justify-content: center;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
`;

const IconWrapper = styled.div<{ customColors?: any }>`
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: ${props => props.customColors?.primary ?
    props.customColors.primary + '80' :
    '#dcfce7'
  };
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
  box-shadow: 0 4px 16px rgba(16, 185, 129, 0.2);
`;

const Title = styled.h3<{ customColors?: any }>`
  font-size: 20px;
  font-weight: 800;
  margin-bottom: 12px;
  color: ${props => props.customColors?.primary || '#059669'};
`;

const Description = styled.p<{ customColors?: any }>`
  font-size: 14px;
  margin-bottom: 24px;
  line-height: 1.5;
  color: ${props => props.customColors?.primary || '#059669'};
`;

interface PromoterInviteCTAProps {
  customColors?: any;
}

export const PromoterInviteCTA: React.FC<PromoterInviteCTAProps> = observer(({ customColors }) => {
  const colors = customColors || fundraiserStore.builderData?.customColors || {};

  const handleDonateClick = () => {
    // Scroll to donation form or open modal depending on configuration
    const donationFormType = fundraiserStore.builderData?.donationFormType || DonationFormType.Embedded;

    if (donationFormType === DonationFormType.Embedded) {
      // Look for join form instead since this is promoter invite page
      const joinForm = document.querySelector('[data-join-form]');
      if (joinForm) {
        const targetPosition = joinForm.getBoundingClientRect().top + window.scrollY - 125;
        window.scrollTo({ top: targetPosition, behavior: 'smooth' });
      }
    }
  };

  // Get button text from builder data
  const getButtonText = () => {
    return fundraiserStore.builderData?.ctaButtonText || 'Donate';
  };

  if (!fundraiserStore.builderData?.ctaTitle && !fundraiserStore.builderData?.ctaDescription) {
    return null;
  }
  // Determine if icon should be shown
  const shouldShowIcon = fundraiserStore.builderData?.ctaIconImageChoice !== IconType.None;
  return (
    <CTAContainer customColors={colors}>
      <IconWrapper customColors={colors} style={{ display: shouldShowIcon ? 'flex' : 'none' }}>
        <Heart size={32} color={colors.primary || '#10b981'} />
      </IconWrapper>

      <Title customColors={colors} style={{
        marginTop: shouldShowIcon ? '0' : '20px',
        marginBottom: shouldShowIcon ? '12px' : '20px'
      }}>
        {fundraiserStore.builderData?.ctaTitle || ''}
      </Title>

      <Description customColors={colors}>
        {fundraiserStore.builderData?.ctaDescription || ''}
      </Description>
    </CTAContainer>
  );
});